import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Editor } from '@tiptap/core'
import { Maximize2, Minimize2, NotebookPen, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Markdown, type MarkdownFeatures } from '@highlight/ui'
import { useMeetingNotepadContent } from '../use-meeting-notepad'
import { NotepadResizeHandle } from './notepad-resize-handle'

/**
 * Rich-text surface tuned for meeting notes: inline emphasis and lists, none
 * of the document furniture. Images stay off because the document syncs to the
 * session's server record on every pause and a pasted screenshot as a data URI
 * would blow its size budget in one keystroke.
 */
const NOTEPAD_FEATURES: MarkdownFeatures = {
  image: false,
  table: false,
  codeBlock: false,
  horizontalRule: false,
  mentions: false,
}

/** Plain-text cap, comfortably inside the server document's 96 KiB budget. */
const NOTEPAD_MAX_LENGTH = 20_000

/**
 * The Meeting View's Notepad (HIG-5829): the user's own notes for the meeting,
 * taking the chat input's place in the footer. The notepad button in the
 * floating bar swaps it in and out; clicking the panel's title row collapses it
 * to that row without giving the slot back, so a glance at the transcript
 * doesn't cost the notes their place.
 *
 * Two buttons on the right of that row take the other two trips: full height,
 * where the notepad grows to everything under the floating bar, and Close,
 * which gives the slot back. Close does what the bar's toggle already does, on
 * purpose: at full height the notepad is the whole panel, and reaching back out
 * to a 36px circle above it to dismiss what you're looking at is a trip the
 * surface should offer itself.
 *
 * Fills whatever box `SidepanelFooterSlot` sizes for it — that box is what
 * animates, so the swap reads as the chat input morphing rather than being
 * replaced. Mounted by the footer rather than the view, because it replaces
 * panel chrome (the same reason `SidepanelChatInput`'s scoped modes live there).
 *
 * Appears and leaves with no fade of its own, deliberately. Both it and the chat
 * input are opaque, so a crossfade is two stacked panels ghosting through each
 * other — going in, the input's stale raster reads through (its `icon://` app
 * icon strands as a grey square, see `SidepanelFooterSlot`); coming out, the
 * input is already back at full opacity and shows through the notepad's text.
 * Swapping instantly and letting the height carry it is what reads as a morph.
 *
 * Notes persist locally by meeting and, while a session is live, to the
 * session's server record (HIG-5815), where later staged-action runs and the
 * saved note's summary can read them.
 */
export function MeetingViewNotepad() {
  const { key, initialNotes, serverMarkdown, updateNotes, isCollapsed, isFull, toggleCollapsed, toggleFull, close } =
    useMeetingNotepadContent()

  return (
    <div
      className={twMerge(
        // inset-x-3/bottom-3 is the same float the chat input gives itself
        // while a View is open; the slot's height allows for it.
        // pt-5, not the p-3 the other three sides keep: the grab handle lives in
        // the top padding, and a gutter that fits a card edge doesn't fit a
        // grabber with room to read as one above the title row.
        'corner-smooth absolute inset-x-3 bottom-3 top-0 flex flex-col gap-4 overflow-hidden rounded-2xl border px-3 pb-3 pt-5',
        // Opaque: it floats over the chat input it replaced, which is still
        // mounted underneath holding the panel's window-scoped effects.
        'bg-background focus-within:border-border border-transparent transition-colors',
        'shadow-lg',
      )}
    >
      <NotepadResizeHandle />

      {/* `-mx-1.5 -my-1` eats half the panel's own p-3 so the row's targets run
            most of its width — leaving 6px so they don't crowd the border —
            while the icon and label stay on the notes' left edge. */}
      <div className="-mx-1.5 -my-1 flex h-7 shrink-0 items-center gap-1">
        {/* The title row is the collapse trigger, like a sidepanel section
              header — the hover pill is what reads as the target. The two
              buttons sit outside it rather than in it: a button can't nest. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!isCollapsed}
          aria-controls="meeting-notepad-editor"
          className={twMerge(
            // bg-transparent: preflight is off, so a bare button falls back to
            // the UA's grey ButtonFace fill.
            // The h-7 control radius is also the concentric radius for this 6px
            // inset inside the panel's own 20px corners.
            'corner-squircle flex h-7 min-w-0 cursor-pointer items-center gap-1.5 rounded-[var(--radius-size-md)] bg-transparent px-1.5',
            'hover:bg-element-hover transition-colors duration-150 motion-reduce:transition-none',
          )}
        >
          <NotebookPen aria-hidden="true" className="text-foreground size-3.5 shrink-0" />
          <h2 id="meeting-notepad-label" className="text-foreground type-ui-sm truncate font-medium">
            Notepad
          </h2>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          <NotepadHeaderButton
            label={isFull ? 'Exit full height' : 'Expand to full height'}
            pressed={isFull}
            onClick={toggleFull}
          >
            {isFull ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
          </NotepadHeaderButton>
          <NotepadHeaderButton label="Close notepad" onClick={close}>
            <X aria-hidden="true" />
          </NotepadHeaderButton>
        </div>
      </div>

      {/* Keyed by meeting so switching remounts a fresh document; kept mounted
            while collapsed so the caret and undo history survive a peek at the
            transcript. */}
      <NotepadEditor
        key={key}
        initialNotes={initialNotes}
        serverMarkdown={serverMarkdown}
        updateNotes={updateNotes}
        isCollapsed={isCollapsed}
      />
    </div>
  )
}

interface NotepadHeaderButtonProps {
  /** Accessible name — these are icon-only, so it's the only name they have. */
  label: string
  /** Present on a button that turns something on and off, absent on an action. */
  pressed?: boolean
  onClick: () => void
  children: ReactNode
}

/**
 * One of the notepad's own controls, sized to the title row beside it. Square
 * and 28px rather than the glyph it holds, so the target clears the 24px
 * floor with the icons still reading as chrome next to the label.
 */
function NotepadHeaderButton({ label, pressed, onClick, children }: NotepadHeaderButtonProps) {
  return (
    <Button
      variant="ghost"
      size="md"
      shape="square"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className={twMerge(
        'text-foreground-secondary hover:text-foreground shrink-0',
        // The pressed state reads as held down rather than merely hovered —
        // otherwise "full height" looks the same on and off.
        pressed && 'bg-element-active text-foreground',
      )}
    >
      {children}
    </Button>
  )
}

interface NotepadEditorProps {
  initialNotes: string
  serverMarkdown: string | null
  updateNotes: (markdown: string) => void
  isCollapsed: boolean
}

/**
 * The editor is uncontrolled: the debounced flush takes every change and a
 * controlled value would re-render the panel just to hand the editor back
 * text it already has. Local store and server document are the same markdown.
 */
export function NotepadEditor({ initialNotes, serverMarkdown, updateNotes, isCollapsed }: NotepadEditorProps) {
  const [editor, setEditor] = useState<Editor | null>(null)

  // The server document only ever seeds an EMPTY editor — the local copy on a
  // machine that has one is at least as fresh (it wrote the server copy), and
  // clobbering visible text over a fetch race is worse than being a write
  // behind. Fills the fresh-machine and post-restart cases the local store
  // can't cover.
  const seededRef = useRef(false)
  useEffect(() => {
    if (!editor || seededRef.current || !serverMarkdown) return
    seededRef.current = true
    if (!editor.isEmpty) return
    editor.commands.setContent(serverMarkdown, { contentType: 'markdown', emitUpdate: false })
  }, [editor, serverMarkdown])

  // Single-purpose editor the user just asked for — land the caret in it.
  useEffect(() => {
    if (!isCollapsed) editor?.commands.focus('end')
  }, [editor, isCollapsed])

  return (
    <div
      id="meeting-notepad-editor"
      aria-labelledby="meeting-notepad-label"
      aria-hidden={isCollapsed}
      className="min-h-0 w-full flex-1 overflow-y-auto"
    >
      <Markdown
        editable
        toolbar={false}
        size="sm"
        defaultValue={initialNotes}
        placeholder="Write your own notes for the meeting…"
        features={NOTEPAD_FEATURES}
        maxLength={NOTEPAD_MAX_LENGTH}
        onChange={(markdown) => updateNotes(markdown)}
        editorRef={setEditor}
        // p-px: the panel's padding carries the inset, so the notes line up
        // with the header's negative margins; the 1px left is the caret's, so
        // it isn't clipped sitting at the start of an empty first line.
        className="min-h-full p-px"
      />
    </div>
  )
}
